import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import TryOurTools from '../components/TryOurTools';

const getVideoId = (input: string): string | null => {
  // Try to extract video ID from various YouTube URL formats
  try {
    const url = new URL(input);
    if (url.hostname.endsWith('youtube.com')) {
      return url.searchParams.get('v');
    }
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1);
    }
  } catch {
    // Not a URL, maybe a raw ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
  }
  return null;
};

const thumbnailResolutions = [
  { label: 'Max Resolution', key: 'maxresdefault' },
  { label: 'Standard (SD)', key: 'sddefault' },
  { label: 'High (HQ)', key: 'hqdefault' },
  { label: 'Medium (MQ)', key: 'mqdefault' },
  { label: 'Default', key: 'default' },
];

const ThumbnailDownloader: React.FC = () => {
  const [input, setInput] = useState('');
  const [videoId, setVideoId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = getVideoId(input.trim());
    if (!id) {
      setError('Please enter a valid YouTube URL or video ID.');
      setVideoId(null);
    } else {
      setError('');
      setVideoId(id);
    }
  };

  return (
    <>
      <Helmet>
        <title>YouTube Thumbnail Downloader - Free & Instant</title>
        <meta name="description" content="Download any YouTube video thumbnail in high quality. Enter a YouTube URL or video ID to get instant access to all available thumbnail images." />
        <meta name="keywords" content="youtube thumbnail downloader, download youtube thumbnail, get youtube thumbnail, youtube image extractor" />
        <link rel="canonical" href="https://youtubeplayer.com/thumbnail-downloader" />
      </Helmet>
      <div className="max-w-xl mx-auto py-10">
        <h1 className="text-3xl font-bold mb-2 text-light-text dark:text-dark-text text-center">YouTube Thumbnail Downloader</h1>
        <p className="text-center text-light-subtle dark:text-dark-subtle mb-8">Download any YouTube video thumbnail in full quality. 100% free, no watermark.</p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Paste YouTube URL or video ID"
            className="flex-1 px-4 py-3 rounded-lg border border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent placeholder-light-subtle dark:placeholder-dark-subtle"
            autoFocus
          />
          <button
            type="submit"
            className="px-6 py-3 rounded-lg bg-light-accent text-white dark:bg-dark-accent dark:text-dark-background font-semibold text-base hover:bg-light-accent_hover dark:hover:bg-dark-accent_hover transition-colors"
          >
            Get Thumbnails
          </button>
        </form>
        {error && <div className="text-red-600 dark:text-red-400 mb-4 text-center">{error}</div>}
        {videoId && (
          <div className="space-y-6">
            {thumbnailResolutions.map(res => (
              <div key={res.key} className="flex flex-col sm:flex-row items-center gap-4 bg-black/5 dark:bg-white/5 rounded-lg p-4">
                <img
                  src={`https://img.youtube.com/vi/${videoId}/${res.key}.jpg`}
                  alt={`${res.label} thumbnail`}
                  className="w-full sm:w-64 rounded shadow border border-light-border dark:border-dark-border bg-white dark:bg-dark-card"
                  loading="lazy"
                  style={{ maxHeight: 180, objectFit: 'contain' }}
                />
                <div className="flex-1 flex flex-col items-center sm:items-start">
                  <div className="font-medium text-light-text dark:text-dark-text mb-2">{res.label}</div>
                  <a
                    href={`https://img.youtube.com/vi/${videoId}/${res.key}.jpg`}
                    download={`youtube-thumbnail-${videoId}-${res.key}.jpg`}
                    className="inline-block px-4 py-2 rounded-lg bg-light-accent text-white dark:bg-dark-accent dark:text-dark-background font-semibold text-sm hover:bg-light-accent_hover dark:hover:bg-dark-accent_hover transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-8 mb-2 p-4 rounded-lg bg-gradient-to-r from-red-100 to-purple-100 dark:from-red-900/30 dark:to-purple-900/30 border border-light-border dark:border-dark-border text-light-text dark:text-dark-text shadow">
          <strong>YouTube Thumbnail Downloader:</strong> Instantly download <b>YouTube video thumbnails</b> in high quality. Enter any YouTube URL or video ID to get the thumbnail image for free. No login or API required. Perfect for bloggers, designers, and content creators who need <b>YouTube thumbnails</b> for previews, presentations, or social media. 100% free and SEO-friendly.
        </div>
        <TryOurTools />
      </div>
    </>
  );
};

export default ThumbnailDownloader; 